import { useEffect, useRef, useState, type CSSProperties } from "react";
import {
  reservationSettingsGet,
  reservationSetSetting,
  reservationUpsertHours,
  reservationDeleteHours,
  reservationUpdateExperience,
  type ReservationSettingsPayload,
  type ReservationSmsPhase,
  type ReservationExperienceAdmin,
} from "../../lib/api";

/* Reservation back-office settings — rendered inside HostSettings for
   owners. Hours are edited venue-wide (one row per weekday, written
   to every room) because all rooms share service hours today; per-room hours
   can come later without changing the backend, which is already per-room. */

const DAY_LABELS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const PHASE_LABELS: Record<ReservationSmsPhase, string> = {
  off: "Off",
  confirm_only: "Confirm Only",
  full: "Full",
};

const PHASE_DESC: Record<ReservationSmsPhase, string> = {
  off: "No reservation SMS/email sent — UI-only mode. Requests still land on the tablet.",
  confirm_only: "Guests get a 'request received' text + a text when a host confirms.",
  full: "All reservation messages: request received, confirmation, and reminders (P2).",
};

interface DayDraft {
  closed: boolean;
  opens: string;        // "15:00"
  lastSeating: string;  // "20:00"
  saving: boolean;
  msg: { ok: boolean; text: string } | null;
}

export default function ReservationSettingsSection() {
  const [data, setData] = useState<ReservationSettingsPayload | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [phaseWorking, setPhaseWorking] = useState(false);
  const [phaseMsg, setPhaseMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [confirmPhase, setConfirmPhase] = useState<ReservationSmsPhase | null>(null);

  const [rules, setRules] = useState({ lead: "60", leadDays: "1", horizon: "60", grace: "15", ceiling: "0.7" });
  const [rulesWorking, setRulesWorking] = useState(false);
  const [rulesMsg, setRulesMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const [days, setDays] = useState<Record<number, DayDraft>>({});

  const [expDrafts, setExpDrafts] = useState<Record<string, {
    min: string; max: string; active: boolean; policy: string; saving: boolean;
    msg: { ok: boolean; text: string } | null;
  }>>({});

  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const load = () =>
    reservationSettingsGet()
      .then((d) => {
        if (!mounted.current) return;
        setData(d);
        setRules({
          lead: String(d.settings.min_lead_minutes ?? 60),
          leadDays: String(d.settings.min_lead_days ?? 1),
          horizon: String(d.settings.booking_horizon_days ?? 60),
          grace: String(d.settings.no_show_grace_minutes ?? 15),
          ceiling: String(d.settings.booking_ceiling ?? 0.7),
        });
        const byDay: Record<number, DayDraft> = {};
        for (let wd = 0; wd < 7; wd++) {
          const row = d.service_hours.find((h) => h.weekday === wd && h.is_active);
          byDay[wd] = row
            ? { closed: false, opens: row.opens_at.slice(0, 5), lastSeating: row.last_seating_at.slice(0, 5), saving: false, msg: null }
            : { closed: true, opens: "15:00", lastSeating: "20:00", saving: false, msg: null };
        }
        setDays(byDay);
        const ed: typeof expDrafts = {};
        for (const e of d.experiences) {
          ed[e.id] = {
            min: String(e.party_size_min), max: String(e.party_size_max),
            active: e.is_active, policy: e.policy_text ?? "", saving: false, msg: null,
          };
        }
        setExpDrafts(ed);
      })
      .catch((e) => mounted.current && setLoadErr((e as Error).message || "Failed to load reservation settings"));

  useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loadErr) return <section style={s.section}><h2 style={s.h2}>Reservations</h2><p style={s.error}>{loadErr}</p></section>;
  if (!data) return <section style={s.section}><h2 style={s.h2}>Reservations</h2><p style={s.muted}>Loading…</p></section>;

  const phase: ReservationSmsPhase = (data.settings.reservation_sms_phase as ReservationSmsPhase) ?? "off";

  const applyPhase = async (next: ReservationSmsPhase) => {
    setConfirmPhase(null);
    setPhaseWorking(true);
    setPhaseMsg(null);
    try {
      await reservationSetSetting("reservation_sms_phase", next);
      setData((d) => d && ({ ...d, settings: { ...d.settings, reservation_sms_phase: next } }));
      setPhaseMsg({ ok: true, text: `Reservation SMS phase set to ${PHASE_LABELS[next]}.` });
    } catch (e) {
      setPhaseMsg({ ok: false, text: (e as Error).message || "Failed to update phase" });
    } finally {
      setPhaseWorking(false);
    }
  };

  const saveRules = async () => {
    const lead = Number(rules.lead), leadDays = Number(rules.leadDays), horizon = Number(rules.horizon),
      grace = Number(rules.grace), ceiling = Number(rules.ceiling);
    if (!Number.isInteger(lead) || lead < 0 || lead > 1440) return setRulesMsg({ ok: false, text: "Lead must be 0–1440 minutes." });
    if (!Number.isInteger(leadDays) || leadDays < 0 || leadDays > 30) return setRulesMsg({ ok: false, text: "Lead days must be 0–30." });
    if (!Number.isInteger(horizon) || horizon < 1 || horizon > 365) return setRulesMsg({ ok: false, text: "Horizon must be 1–365 days." });
    if (!Number.isInteger(grace) || grace < 0 || grace > 120) return setRulesMsg({ ok: false, text: "Grace must be 0–120 minutes." });
    if (!(ceiling >= 0.1 && ceiling <= 1)) return setRulesMsg({ ok: false, text: "Ceiling must be 0.1–1." });
    setRulesWorking(true);
    setRulesMsg(null);
    try {
      await reservationSetSetting("min_lead_minutes", lead);
      await reservationSetSetting("min_lead_days", leadDays);
      await reservationSetSetting("booking_horizon_days", horizon);
      await reservationSetSetting("no_show_grace_minutes", grace);
      await reservationSetSetting("booking_ceiling", ceiling);
      setRulesMsg({ ok: true, text: "Booking rules saved." });
    } catch (e) {
      setRulesMsg({ ok: false, text: (e as Error).message || "Failed to save rules" });
    } finally {
      setRulesWorking(false);
    }
  };

  const patchDay = (wd: number, patch: Partial<DayDraft>) =>
    setDays((d) => ({ ...d, [wd]: { ...d[wd], ...patch } }));

  const saveDay = async (wd: number) => {
    const draft = days[wd];
    if (!draft) return;
    if (!draft.closed && draft.opens >= draft.lastSeating) {
      return patchDay(wd, { msg: { ok: false, text: "Open must be before last seating." } });
    }
    patchDay(wd, { saving: true, msg: null });
    try {
      const roomIds = data.rooms.map((r) => r.id);
      if (draft.closed) {
        await Promise.all(roomIds.map((rid) => reservationDeleteHours(rid, wd)));
      } else {
        await Promise.all(roomIds.map((rid) =>
          reservationUpsertHours({ room_id: rid, weekday: wd, opens_at: draft.opens, last_seating_at: draft.lastSeating }),
        ));
      }
      patchDay(wd, { msg: { ok: true, text: "Saved." } });
    } catch (e) {
      patchDay(wd, { msg: { ok: false, text: (e as Error).message || "Save failed" } });
    } finally {
      patchDay(wd, { saving: false });
    }
  };

  const patchExp = (id: string, patch: Partial<(typeof expDrafts)[string]>) =>
    setExpDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));

  const saveExp = async (exp: ReservationExperienceAdmin) => {
    const draft = expDrafts[exp.id];
    if (!draft) return;
    const min = Number(draft.min), max = Number(draft.max);
    if (!Number.isInteger(min) || min < 1 || !Number.isInteger(max) || max < min) {
      return patchExp(exp.id, { msg: { ok: false, text: "Party range must be valid (min ≤ max)." } });
    }
    patchExp(exp.id, { saving: true, msg: null });
    try {
      await reservationUpdateExperience(exp.id, {
        party_size_min: min,
        party_size_max: max,
        is_active: draft.active,
        policy_text: draft.policy,
      });
      patchExp(exp.id, { msg: { ok: true, text: "Saved." } });
    } catch (e) {
      patchExp(exp.id, { msg: { ok: false, text: (e as Error).message || "Save failed" } });
    } finally {
      patchExp(exp.id, { saving: false });
    }
  };

  return (
    <>
      {/* ── Reservation SMS phase ── */}
      <section style={s.section}>
        <h2 style={s.h2}>Reservations — SMS Phase</h2>
        <p style={s.desc}>{PHASE_DESC[phase]}</p>
        <div style={s.phaseRow}>
          {(["off", "confirm_only", "full"] as ReservationSmsPhase[]).map((p) => (
            <button
              key={p} type="button" disabled={phaseWorking}
              onClick={() => {
                if (p === phase) return;
                if (p === "off") void applyPhase("off"); // turning sends OFF needs no warning
                else setConfirmPhase(p);
              }}
              style={{ ...s.phaseBtn, ...(phase === p ? s.phaseBtnActive : {}) }}
            >
              {PHASE_LABELS[p]}
            </button>
          ))}
        </div>
        {confirmPhase && confirmPhase !== "off" && (
          <div style={s.warnBox}>
            <p style={s.warnText}>
              ⚠️ Turning reservation SMS on before phone verification (OTP) ships means the public
              /reserve form can trigger texts to unverified numbers (toll-fraud risk). Are you sure?
            </p>
            <div style={s.phaseRow}>
              <button type="button" style={s.btnDanger} onClick={() => void applyPhase(confirmPhase)}>
                Yes, set {PHASE_LABELS[confirmPhase]}
              </button>
              <button type="button" style={s.btnSecondary} onClick={() => setConfirmPhase(null)}>Cancel</button>
            </div>
          </div>
        )}
        {phaseMsg && <p style={phaseMsg.ok ? s.ok : s.error}>{phaseMsg.text}</p>}
      </section>

      {/* ── Booking rules ── */}
      <section style={s.section}>
        <h2 style={s.h2}>Reservations — Booking Rules</h2>
        <p style={s.desc}>
          Min lead (days) is how far ahead guests must book: 1 = reservations close the night
          before, so same-day guests go on the walk-in waitlist. Set 0 to allow same-day booking
          again (then only the minutes lead applies).
        </p>
        <div style={s.ruleGrid}>
          <label style={s.ruleLabel}>Min lead (days)
            <input style={s.numInput} inputMode="numeric" value={rules.leadDays}
              onChange={(e) => setRules((r) => ({ ...r, leadDays: e.target.value }))} />
          </label>
          <label style={s.ruleLabel}>Min lead (minutes)
            <input style={s.numInput} inputMode="numeric" value={rules.lead}
              onChange={(e) => setRules((r) => ({ ...r, lead: e.target.value }))} />
          </label>
          <label style={s.ruleLabel}>Horizon (days)
            <input style={s.numInput} inputMode="numeric" value={rules.horizon}
              onChange={(e) => setRules((r) => ({ ...r, horizon: e.target.value }))} />
          </label>
          <label style={s.ruleLabel}>No-show grace (min)
            <input style={s.numInput} inputMode="numeric" value={rules.grace}
              onChange={(e) => setRules((r) => ({ ...r, grace: e.target.value }))} />
          </label>
          <label style={s.ruleLabel}>Booking ceiling (0–1)
            <input style={s.numInput} inputMode="decimal" value={rules.ceiling}
              onChange={(e) => setRules((r) => ({ ...r, ceiling: e.target.value }))} />
          </label>
        </div>
        <button type="button" style={s.btnPrimary} disabled={rulesWorking} onClick={() => void saveRules()}>
          {rulesWorking ? "Saving…" : "Save Rules"}
        </button>
        {rulesMsg && <p style={rulesMsg.ok ? s.ok : s.error}>{rulesMsg.text}</p>}
      </section>

      {/* ── Weekly hours ── */}
      <section style={s.section}>
        <h2 style={s.h2}>Reservations — Weekly Hours</h2>
        <p style={s.desc}>
          Bookable seating windows (applies to all rooms). Last seating is the latest reservation
          time — typically closing time minus the 2-hour table limit.
        </p>
        {DAY_LABELS.map((label, wd) => {
          const d = days[wd];
          if (!d) return null;
          return (
            <div key={wd} style={s.dayRow}>
              <span style={s.dayName}>{label}</span>
              <button
                type="button"
                style={{ ...s.toggle, ...(d.closed ? s.toggleOff : s.toggleOn) }}
                onClick={() => patchDay(wd, { closed: !d.closed, msg: null })}
              >
                {d.closed ? "CLOSED" : "OPEN"}
              </button>
              {!d.closed && (
                <>
                  <input type="time" style={s.timeInput} value={d.opens}
                    onChange={(e) => patchDay(wd, { opens: e.target.value })} aria-label={`${label} opens`} />
                  <span style={s.muted}>to</span>
                  <input type="time" style={s.timeInput} value={d.lastSeating}
                    onChange={(e) => patchDay(wd, { lastSeating: e.target.value })} aria-label={`${label} last seating`} />
                </>
              )}
              <button type="button" style={s.btnSecondary} disabled={d.saving} onClick={() => void saveDay(wd)}>
                {d.saving ? "…" : "Save"}
              </button>
              {d.msg && <span style={d.msg.ok ? s.ok : s.error}>{d.msg.text}</span>}
            </div>
          );
        })}
      </section>

      {/* ── Experiences ── */}
      <section style={s.section}>
        <h2 style={s.h2}>Reservations — Experiences</h2>
        {data.experiences.map((exp) => {
          const d = expDrafts[exp.id];
          if (!d) return null;
          return (
            <div key={exp.id} style={s.expCard}>
              <div style={s.expHead}>
                <span style={s.expTitle}>{exp.title}</span>
                <span style={s.muted}>{exp.deposit_model}</span>
                <button
                  type="button"
                  style={{ ...s.toggle, ...(d.active ? s.toggleOn : s.toggleOff) }}
                  onClick={() => patchExp(exp.id, { active: !d.active })}
                >
                  {d.active ? "ACTIVE" : "INACTIVE"}
                </button>
              </div>
              <div style={s.ruleGrid}>
                <label style={s.ruleLabel}>Party min
                  <input style={s.numInput} inputMode="numeric" value={d.min}
                    onChange={(e) => patchExp(exp.id, { min: e.target.value })} />
                </label>
                <label style={s.ruleLabel}>Party max
                  <input style={s.numInput} inputMode="numeric" value={d.max}
                    onChange={(e) => patchExp(exp.id, { max: e.target.value })} />
                </label>
              </div>
              <label style={s.ruleLabel}>Policy text (shown to guests at booking)
                <textarea style={s.textarea} rows={4} value={d.policy}
                  onChange={(e) => patchExp(exp.id, { policy: e.target.value })} />
              </label>
              <button type="button" style={s.btnPrimary} disabled={d.saving} onClick={() => void saveExp(exp)}>
                {d.saving ? "Saving…" : "Save Experience"}
              </button>
              {d.msg && <p style={d.msg.ok ? s.ok : s.error}>{d.msg.text}</p>}
            </div>
          );
        })}
      </section>
    </>
  );
}

const s: Record<string, CSSProperties> = {
  section: { borderBottom: "1px solid var(--border)", paddingBottom: 20, marginBottom: 20 },
  h2: { fontSize: 13, fontWeight: 800, color: "var(--gold)", textTransform: "uppercase", letterSpacing: 0.5, margin: "0 0 8px" },
  desc: { fontSize: 12, color: "var(--muted)", margin: "0 0 12px", lineHeight: 1.5 },
  muted: { color: "var(--muted)", fontSize: 12 },
  ok: { color: "#27ae60", fontSize: 12, margin: "4px 0 0" },
  error: { color: "#E05040", fontSize: 12, margin: "4px 0 0" },
  phaseRow: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 },
  phaseBtn: {
    padding: "8px 16px", borderRadius: 8, border: "1px solid var(--border)",
    background: "transparent", color: "var(--muted)", fontSize: 13, fontWeight: 700, cursor: "pointer",
  },
  phaseBtnActive: { borderColor: "var(--gold)", color: "var(--gold)", background: "rgba(249,175,54,0.08)" },
  warnBox: {
    border: "1px solid rgba(224,80,64,0.5)", background: "rgba(224,80,64,0.08)",
    borderRadius: 8, padding: "10px 12px", marginBottom: 8,
  },
  warnText: { fontSize: 12, color: "var(--text)", margin: "0 0 10px", lineHeight: 1.5 },
  ruleGrid: { display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 },
  ruleLabel: { display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.4 },
  numInput: {
    width: 110, padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border)",
    background: "var(--bg)", color: "var(--text)", fontSize: 16,
  },
  timeInput: {
    padding: "6px 8px", borderRadius: 6, border: "1px solid var(--border)",
    background: "var(--bg)", color: "var(--text)", fontSize: 14,
  },
  textarea: {
    width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 6,
    border: "1px solid var(--border)", background: "var(--bg)", color: "var(--text)",
    fontSize: 13, lineHeight: 1.5, resize: "vertical", textTransform: "none", letterSpacing: 0,
    fontWeight: 400, marginTop: 4,
  },
  dayRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" },
  dayName: { width: 88, fontSize: 13, fontWeight: 700, color: "var(--text)" },
  toggle: {
    padding: "4px 12px", borderRadius: 20, border: "none", fontSize: 11,
    fontWeight: 800, letterSpacing: 0.5, cursor: "pointer", minWidth: 64,
  },
  toggleOn: { background: "#27ae60", color: "#fff" },
  toggleOff: { background: "var(--border)", color: "var(--muted)" },
  btnPrimary: {
    padding: "8px 16px", borderRadius: 6, border: "none", background: "var(--gold)",
    color: "#0D0B08", fontWeight: 800, fontSize: 12, cursor: "pointer",
  },
  btnSecondary: {
    padding: "8px 16px", borderRadius: 6, border: "1px solid var(--border)",
    background: "transparent", color: "var(--muted)", fontWeight: 700, fontSize: 12, cursor: "pointer",
  },
  btnDanger: {
    padding: "8px 16px", borderRadius: 6, border: "none", background: "#E05040",
    color: "#fff", fontWeight: 800, fontSize: 12, cursor: "pointer",
  },
  expCard: {
    border: "1px solid var(--border)", borderRadius: 8, padding: "12px",
    marginBottom: 12, display: "flex", flexDirection: "column", gap: 10,
  },
  expHead: { display: "flex", alignItems: "center", gap: 10, justifyContent: "space-between" },
  expTitle: { fontSize: 14, fontWeight: 700, color: "var(--text)" },
};
